import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, User, Phone, MapPin, Shield, LogOut, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Profile() {
  const nav = useNavigate();
  const { logout, user } = useAuth();
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    api.get("/customer/me").then((r) => { setC(r.data.customer); setForm(r.data.customer || {}); });
  }, []);

  const save = async () => {
    try {
      const r = await api.patch("/customer/profile", form);
      setC(r.data); setEdit(false);
      toast.success("Profile updated");
    } catch (e) { toast.error("Failed"); }
  };

  const onLogout = () => { logout(); nav("/login"); };

  if (!c) return <div className="p-6 text-center text-slate-500">Loading...</div>;

  return (
    <div data-testid="profile-page">
      <div className="bg-tenant-gradient text-white px-5 pt-6 pb-12 flex items-center gap-3 rounded-b-[2rem]">
        <button data-testid="header-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="font-extrabold tracking-tight text-lg">My Profile</div>
      </div>

      <div className="px-5 -mt-6 relative z-10">
        <div className="bg-white rounded-3xl p-5 card-shadow-lg flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl gold-grad grid place-items-center text-tenant-primary font-extrabold text-xl">
            {(c.full_name || "U").charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div data-testid="profile-name" className="font-extrabold tracking-tight text-lg">{c.full_name}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> +91 {c.phone}</div>
            <div className="text-xs text-emerald-600 font-bold mt-0.5">Customer · {c.referral_code}</div>
          </div>
          <button data-testid="edit-toggle" onClick={() => setEdit(!edit)} className="text-xs font-bold text-tenant-primary">
            {edit ? "Cancel" : "Edit"}
          </button>
        </div>
      </div>

      <div className="px-5 mt-5 space-y-3">
        <Item icon={User} label="Full Name" value={c.full_name} edit={edit} v={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testId="profile-full-name" />
        <Item icon={Shield} label="Aadhaar" value={c.aadhaar} edit={edit} v={form.aadhaar} onChange={(v) => setForm({ ...form, aadhaar: v })} testId="profile-aadhaar" />
        <Item icon={FileCheck2} label="PAN" value={c.pan} edit={edit} v={form.pan} onChange={(v) => setForm({ ...form, pan: v })} testId="profile-pan" />
        <Item icon={MapPin} label="Address" value={c.address} edit={edit} v={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="profile-address" />
        <Item icon={User} label="Nominee" value={c.nominee_name} edit={edit} v={form.nominee_name} onChange={(v) => setForm({ ...form, nominee_name: v })} testId="profile-nominee" />

        {edit && (
          <button data-testid="profile-save" onClick={save} className="w-full bg-tenant-primary text-white py-3.5 rounded-2xl font-bold tap-scale">
            Save Changes
          </button>
        )}

        <button data-testid="customer-logout" onClick={onLogout} className="w-full mt-4 bg-rose-50 text-rose-600 py-3.5 rounded-2xl font-bold tap-scale inline-flex items-center justify-center gap-2">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
      <div className="h-10" />
    </div>
  );
}

const Item = ({ icon: Icon, label, value, edit, v, onChange, testId }) => (
  <div className="bg-white border border-slate-100 rounded-2xl p-3.5 card-shadow flex items-center gap-3">
    <div className="w-9 h-9 rounded-xl bg-blue-50 text-tenant-primary grid place-items-center"><Icon className="w-4 h-4" /></div>
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">{label}</div>
      {edit ? (
        <input data-testid={testId} value={v || ""} onChange={(e) => onChange(e.target.value)} className="mt-0.5 w-full outline-none font-bold text-sm border-b border-slate-200" />
      ) : (
        <div className="font-bold text-sm text-slate-800 truncate">{value || "—"}</div>
      )}
    </div>
  </div>
);
